import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, FileSpreadsheet } from "lucide-react";
import PageHero from "../components/ui-custom/PageHero";
import GlassCard from "../components/ui-custom/GlassCard";
import Reveal from "../components/ui-custom/Reveal";
import CellValue from "../components/ui-custom/CellValue";
import EsgSections from "../components/ui-custom/EsgSections";
import { useAuth } from "../context/AuthContext";
import { formatDateTime } from "../lib/formatDate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const RawDataTable = ({ tab }) => (
  <div className="mb-6 last:mb-0">
    <h4 className="font-display font-semibold text-sm text-foreground mb-3">{tab.name}</h4>
    {tab.rows.length === 0 ? (
      <p className="text-sm text-muted-foreground">No rows added yet.</p>
    ) : (
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className="text-left text-muted-foreground uppercase text-xs tracking-wide border-b border-border/10">
            {tab.columns.map((c) => (
              <th key={c} className="px-4 py-2 break-words">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tab.rows.map((row, i) => (
            <tr key={i} className="border-b border-border/5">
              {tab.columns.map((c) => (
                <td key={c} className="px-4 py-2 text-muted-foreground break-words align-top">
                  <CellValue value={row[c]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const SiteDetail = () => {
  const { siteId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    setError("");
    axios
      .get(`${API}/sustainability/sites/${siteId}/data`, { withCredentials: true, timeout: 40000 })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || "Could not load this site's data right now."));
  }, [siteId]);

  return (
    <div>
      <PageHero
        eyebrow="Sustainability"
        title={data ? data.name : "Site"}
        subtitle="Environmental and energy performance tracking for this site, updated directly from its own records."
      />

      <section className="px-6 pb-28">
        <div className="max-w-4xl mx-auto space-y-5">
          <Link
            to="/sustainability"
            className="inline-flex items-center gap-2 text-xs font-semibold text-qblue uppercase tracking-wide hover:underline"
          >
            <ArrowLeft size={14} /> All Sites
          </Link>

          {error && (
            <GlassCard className="p-8 text-center">
              <p className="text-foreground font-medium">{error}</p>
            </GlassCard>
          )}

          {!data && !error && <p className="text-sm text-muted-foreground text-center">Loading…</p>}

          {data && (
            <Reveal>
              <GlassCard className="p-7">
                {data.sheet_url && user?.is_employee && (
                  <a
                    href={data.sheet_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-qcyan hover:underline mb-5"
                  >
                    <FileSpreadsheet size={14} /> Open Sheet <ExternalLink size={12} />
                  </a>
                )}

                <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                  <p className="section-eyebrow">ESG Snapshot</p>
                  {data.fetched_at && (
                    <p className="text-xs text-muted-foreground">Data as of {formatDateTime(data.fetched_at)}</p>
                  )}
                </div>
                <h3 className="font-display font-semibold text-foreground text-lg mb-5">
                  Monthly Environment, Social &amp; Governance Data
                </h3>
                <EsgSections monthly={data.monthly} />
              </GlassCard>
            </Reveal>
          )}

          {data && data.tabs.length > 0 && (
            <Reveal>
              <GlassCard className="p-7">
                <p className="section-eyebrow mb-4">Raw Records</p>
                {data.tabs.map((tab) => (
                  <RawDataTable key={tab.name} tab={tab} />
                ))}
              </GlassCard>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
};

export default SiteDetail;
